import { ArrowLeft, Download, EyeOff, MessageSquare } from "lucide-react";
import { useMemo } from "react";

import { EmptyState, LoadingRows } from "components/states";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { csvFileName, downloadCsv, feedbackCsv } from "../../feedback/csv";
import { summarize, type QuestionSummary } from "../../feedback/logic";
import {
  MIN_ANONYMOUS_RESULTS,
  type FeedbackForm,
  type FeedbackResponse,
} from "../../feedback/schemas";
import { useFeedbackResponses } from "../../feedback/use-feedback";
import { useAttendeeRecords } from "../use-attendee-records";

/**
 * Results for one form. Named and anonymous take separate paths on purpose:
 * only the named one ever reads the roster, so an anonymous form's results are
 * never so much as rendered next to a list of names.
 */
export function FeedbackResults({
  eventId,
  form,
  onBack,
}: {
  eventId: string;
  form: FeedbackForm;
  onBack: () => void;
}) {
  return (
    <div className="max-w-3xl min-w-0 space-y-5" data-testid="feedback-results">
      <Button variant="ghost" className="h-11 px-2" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        All feedback forms
      </Button>
      <h2 className="text-lg font-semibold text-foreground wrap-anywhere">
        {form.title}
      </h2>
      {form.anonymous ? (
        <AnonymousResults eventId={eventId} form={form} />
      ) : (
        <NamedResults eventId={eventId} form={form} />
      )}
    </div>
  );
}

function AnonymousResults({
  eventId,
  form,
}: {
  eventId: string;
  form: FeedbackForm;
}) {
  const visible = form.responseCount >= MIN_ANONYMOUS_RESULTS;
  const responses = useFeedbackResponses(eventId, form.id, visible);

  if (!visible) {
    return (
      <EmptyState
        icon={EyeOff}
        title={`Results appear once ${MIN_ANONYMOUS_RESULTS} people have answered`}
        testId="feedback-results-hidden"
      >
        This form is anonymous. With fewer answers it would be too easy to tell
        who wrote what. {form.responseCount} so far. Answers are added in
        batches every five minutes, so a new one can take a while to count.
      </EmptyState>
    );
  }
  return (
    <ResultsBody
      form={form}
      responses={responses}
      respondentName={() => ""}
      notice="Anonymous: answers are stored without names and added in batches every five minutes. A response shows only the window it arrived in, which can point to one person if they answered alone in it."
    />
  );
}

function NamedResults({
  eventId,
  form,
}: {
  eventId: string;
  form: FeedbackForm;
}) {
  const responses = useFeedbackResponses(eventId, form.id, true);
  const attendees = useAttendeeRecords(eventId);
  const names = useMemo(
    () =>
      new Map(
        attendees.status === "success"
          ? attendees.data.map((attendee) => [attendee.id, attendee.name])
          : [],
      ),
    [attendees],
  );
  const respondentName = (response: FeedbackResponse) =>
    (response.attendeeId && names.get(response.attendeeId)) ||
    "Former attendee";

  return (
    <ResultsBody
      form={form}
      responses={responses}
      respondentName={respondentName}
    />
  );
}

function ResultsBody({
  form,
  responses,
  respondentName,
  notice,
}: {
  form: FeedbackForm;
  responses: ReturnType<typeof useFeedbackResponses>;
  respondentName: (response: FeedbackResponse) => string;
  notice?: string;
}) {
  if (responses.status === "loading") return <LoadingRows rows={4} />;
  if (responses.status === "error") {
    return (
      <EmptyState icon={MessageSquare} title="Results could not be loaded">
        {responses.error}
      </EmptyState>
    );
  }

  const data = responses.data;
  const summaries = summarize(form.questions, data);
  const byId = new Map(data.map((response) => [response.id, response]));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p
          className="text-sm font-medium text-foreground"
          data-testid="feedback-response-count"
        >
          {data.length} {data.length === 1 ? "response" : "responses"}
        </p>
        {notice && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <EyeOff className="h-4 w-4" aria-hidden="true" />
            {notice}
          </p>
        )}
        <Button
          variant="outline"
          className="ml-auto h-11"
          disabled={data.length === 0}
          onClick={() =>
            downloadCsv(
              csvFileName(form.title),
              feedbackCsv(form, data, respondentName),
            )
          }
          data-testid="feedback-export-csv"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {data.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No responses yet">
          Answers appear here as attendees send them.
        </EmptyState>
      ) : (
        summaries.map((summary, index) => (
          <SummaryCard
            key={summary.question.id}
            number={index + 1}
            summary={summary}
            nameOf={
              form.anonymous
                ? undefined
                : (responseId) => {
                    const response = byId.get(responseId);
                    return response ? respondentName(response) : "";
                  }
            }
          />
        ))
      )}
    </div>
  );
}

function SummaryCard({
  number,
  summary,
  nameOf,
}: {
  number: number;
  summary: QuestionSummary;
  nameOf?: (responseId: string) => string;
}) {
  return (
    <Card data-testid={`feedback-summary-${number}`}>
      <CardContent className="space-y-3 p-4">
        <div className="min-w-0">
          <p className="font-medium text-foreground wrap-anywhere">
            {number}. {summary.question.label}
          </p>
          <p className="text-xs text-muted-foreground">
            {summary.answered} {summary.answered === 1 ? "answer" : "answers"}
            {summary.kind === "rating" && summary.average !== null && (
              <>
                {" · "}
                <span data-testid={`feedback-average-${number}`}>
                  average {summary.average.toFixed(1)}
                </span>
              </>
            )}
          </p>
        </div>

        {summary.kind === "rating" && (
          <Bars
            total={summary.answered}
            rows={summary.counts.map((count, index) => ({
              key: String(index + 1),
              label: String(index + 1),
              count,
            }))}
          />
        )}
        {summary.kind === "choice" && (
          <Bars
            total={summary.answered}
            rows={summary.counts.map((row) => ({
              key: row.optionId,
              label: row.label,
              count: row.count,
            }))}
          />
        )}
        {summary.kind === "text" && (
          <ul className="space-y-2">
            {summary.answers.map((answer) => (
              <li
                key={answer.responseId}
                className="rounded-md border border-border bg-muted px-3 py-2 text-sm"
              >
                <p className="whitespace-pre-line wrap-anywhere text-foreground">
                  {answer.text}
                </p>
                {nameOf && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {nameOf(answer.responseId)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Bars({
  total,
  rows,
}: {
  total: number;
  rows: { key: string; label: string; count: number }[];
}) {
  return (
    <ul className="space-y-1.5">
      {rows.map((row) => {
        const share = total === 0 ? 0 : Math.round((row.count / total) * 100);
        return (
          <li
            key={row.key}
            className="grid grid-cols-[minmax(0,1fr)_3rem] gap-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">{row.label}</p>
              <div className="mt-1 h-2 rounded-full bg-muted ring-1 ring-inset ring-border">
                <div
                  className="h-2 rounded-full bg-dusk-chapter-1 ring-1 ring-inset ring-lit/40"
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
            <p className="text-right text-sm tabular-nums text-muted-foreground">
              {row.count}
            </p>
          </li>
        );
      })}
    </ul>
  );
}
